import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { createColumnHelper } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import { DataTable, FAIcon, IconTooltip } from '@/components'
import { useVehicleExpirationTypeMutations } from '@/modules/vehicles/fleet/mutations.hooks'
import { VEHICLE_EXPIRATION_TYPES_FOR_SETTINGS_QUERY } from '@/modules/vehicles/queries'
import type {
  VehicleExpirationTypeSettingsNode,
  VehicleExpirationTypesForSettingsQuery,
} from '@/modules/vehicles/types.graphql'
import { getVehicleExpirationTypeName } from '@/modules/vehicles/util'
import { getNodes } from '@/utils/graphql'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import { Header, OldSection, SectionHeader, OldAddButton } from '../common'
import type { VehicleExpirationTypeForm } from '../types'
import { VehicleExpirationTypePrompt } from './vehicle-expiration-type-prompt'

const column = createColumnHelper<VehicleExpirationTypeSettingsNode>()

export const VehicleExpirationTypeSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    expirationTypes: 'settings.vehicle-expiration-types',
    addExpirationType: 'settings.add-vehicle-expiration-type',
    info: 'settings.info.vehicle-expiration-types',

    name: 'common.name',
    expirations: 'vehicles.vehicle-expirations',
    services: 'vehicles.vehicle-services',
    noResults: 'common.no-results',
    builtIn: 'settings.built-in-vehicle-expiration-type',

    prompt: {
      delete: ['settings.prompts.vehicle-expiration-type-delete', { name: '' }],
      deleteInUse: [
        'settings.prompts.vehicle-expiration-type-delete-in-use',
        { name: '', n: 0 },
      ],
      deleteTitle: 'settings.prompts.vehicle-expiration-type-delete-title',
    },
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()
  const mutations = useVehicleExpirationTypeMutations()

  const { data, loading } = useQuery<VehicleExpirationTypesForSettingsQuery>(
    VEHICLE_EXPIRATION_TYPES_FOR_SETTINGS_QUERY,
    {
      variables: { orderBy: 'name' },
      onError: onErrorAuto(),
    }
  )

  const expirationTypes = useMemo(
    () => getNodes(data?.allVehicleExpirationTypes),
    [data]
  )

  // The uniqueness check is case-insensitive server side, and compares the
  // stored name -- which for the built-in types is the untranslated key.
  const takenNames = useMemo(
    () => expirationTypes.map(type => type.name.toLowerCase()),
    [expirationTypes]
  )

  async function handleCreate() {
    const { data } = await addPrompt<VehicleExpirationTypeForm | null>(
      resolve => (
        <VehicleExpirationTypePrompt
          existingNames={takenNames}
          onSubmit={resolve}
        />
      )
    )
    if (!data) return

    await mutations.createVehicleExpirationType({
      variables: { input: { name: data.name } },
    })
  }

  const handleEdit = useCallback(
    async (expirationType: VehicleExpirationTypeSettingsNode) => {
      const { data } = await addPrompt<VehicleExpirationTypeForm | null>(
        resolve => (
          <VehicleExpirationTypePrompt
            initialData={{ name: expirationType.name }}
            existingNames={takenNames.filter(
              name => name !== expirationType.name.toLowerCase()
            )}
            onSubmit={resolve}
          />
        )
      )
      if (!data) return

      await mutations.patchVehicleExpirationType({
        variables: { id: expirationType.id, input: { name: data.name } },
      })
    },
    [addPrompt, mutations, takenNames]
  )

  const handleDelete = useCallback(
    async (expirationType: VehicleExpirationTypeSettingsNode) => {
      const inUse =
        expirationType.expirationsCount + expirationType.servicePlansCount
      const { data: answer } = await confirm(
        inUse > 0
          ? translations.prompt.deleteInUse({
              name: expirationType.name,
              n: inUse,
            })
          : translations.prompt.delete({ name: expirationType.name }),
        translations.prompt.deleteTitle
      )
      if (!answer) return

      await mutations.deleteVehicleExpirationType({
        variables: { id: expirationType.id },
      })
    },
    [confirm, mutations, translations.prompt]
  )

  const columns = useMemo(
    () => [
      // Sorted on the label rather than the stored name, which for the built-in
      // types is an untranslated key ("nextEuControl").
      column.accessor(
        type => getVehicleExpirationTypeName({ name: type.name }),
        {
          id: 'name',
          header: translations.name,
          enableSorting: true,
          cell(info) {
            const expirationType = info.row.original

            return (
              <span className="flex items-center gap-2">
                {info.getValue()}

                {!expirationType.editable && (
                  <IconTooltip
                    icon={{ icon: 'lock' }}
                    triggerClassName="text-xs text-stone-400"
                  >
                    {translations.builtIn}
                  </IconTooltip>
                )}
              </span>
            )
          },
        }
      ),
      column.accessor('expirationsCount', {
        header: translations.expirations,
        enableSorting: true,
      }),
      column.accessor('servicePlansCount', {
        header: translations.services,
        enableSorting: true,
      }),
      column.display({
        id: 'edit',
        meta: { narrow: true },
        cell(info) {
          const expirationType = info.row.original
          if (!expirationType.editable) return null

          return (
            <FAIcon
              className="text-gray-400 hover:text-secondary"
              icon="edit"
              cursor="pointer"
              onClick={() => handleEdit(expirationType)}
            />
          )
        },
      }),
      column.display({
        id: 'delete',
        meta: { narrow: true },
        cell(info) {
          const expirationType = info.row.original
          if (!expirationType.editable) return null

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => handleDelete(expirationType)}
            />
          )
        },
      }),
    ],
    [
      handleDelete,
      handleEdit,
      translations.builtIn,
      translations.expirations,
      translations.name,
      translations.services,
    ]
  )

  return (
    <OldSection>
      <Header>
        <SectionHeader loading={loading || mutations.loading}>
          {translations.expirationTypes}
        </SectionHeader>

        <OldAddButton
          addText={translations.addExpirationType}
          adding={false}
          onClick={handleCreate}
        />
      </Header>

      <p className="mb-4 text-sm text-stone-500">{translations.info}</p>

      <DataTable
        columns={columns}
        data={expirationTypes}
        noDataText={translations.noResults}
      />
    </OldSection>
  )
}
